import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import useDispatch from "@/Components/Utils/useDispatch";
import { fetchCurrencyConfig } from "@/Actions/Settings/currencyConfig";
import type { CurrencyConfigurationView } from "@/Contracts";
import type { GlobalState } from "@/Reducers/interface";

interface UseCurrencyConfigResult {
	config: CurrencyConfigurationView | null;
	loading: boolean;
	error: string | null;
	refetch: () => void;
}

// Module-scoped flag: many components call the hook in the same render
// pass, so Redux's `loading=true` hasn't propagated yet when each useEffect
// fires. This guard ensures only the first caller dispatches the fetch.
let fetchInFlight = false;

export const useCurrencyConfig = (): UseCurrencyConfigResult => {
	const dispatch = useDispatch();

	const currencyConfig = useSelector(
		(state: GlobalState) => state.settings.currencyConfig
	);
	const currencyConfigLoading = useSelector(
		(state: GlobalState) => state.settings.currencyConfigLoading
	);
	const currencyConfigError = useSelector(
		(state: GlobalState) => state.settings.currencyConfigError
	);

	// Mirror Redux's loading state into the module flag. Only clear on a
	// definitive end state (config loaded or error) — never on the
	// "no-state-yet" path, otherwise the initial render before the dispatch
	// effect runs would clear the flag and let the stampede through.
	useEffect(() => {
		if (currencyConfigLoading) fetchInFlight = true;
		else if (currencyConfig !== null || currencyConfigError)
			fetchInFlight = false;
	}, [currencyConfig, currencyConfigLoading, currencyConfigError]);

	useEffect(() => {
		if (
			currencyConfig === null &&
			!currencyConfigLoading &&
			!currencyConfigError &&
			!fetchInFlight
		) {
			fetchInFlight = true;
			dispatch(fetchCurrencyConfig());
		}
	}, [currencyConfig, currencyConfigLoading, currencyConfigError, dispatch]);

	// refetch() is explicit caller intent — always dispatch, never gate on
	// the dedup flag. The sync effect above picks up Redux's loading=true
	// from the resulting GET_REQUEST and propagates to the flag.
	const refetch = useCallback(() => {
		dispatch(fetchCurrencyConfig());
	}, [dispatch]);

	return {
		config: currencyConfig,
		loading: currencyConfigLoading,
		error: currencyConfigError,
		refetch
	};
};
